import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Box, Typography, Card, Chip, IconButton, CardContent, Grid, Tooltip as TooltipCustom, useMediaQuery, Backdrop, CircularProgress, useTheme, Stack } from '@mui/material';
// app UI controller for toggling sidenav
import { useMaterialUIController, setMiniSidenav } from "context";
import KpiMetricscards from "components/KPI/KpiMetricscards";
import TimeSeriescards from "components/TimeSeries/TimeSeriescards";
import PieLegendCard from "components/Visualizations/Piecharts"
import Barcharts from "components/Visualizations/Barcharts"
import Map from 'components/Visualizations/Map'
import SkuSingleBarChart from "components/Visualizations/SingleBarchartSku";
import FilterChip from "components/FilterChip/FilterChip";
import { fetchMarketing } from "services/marketing.service";
import { getTimeSeriesLabels } from "../../../components/TimeSeries/monthUtils";
import { useFilterContext } from "../../../../src/context/FilterContext";
import { fetchMarketingFilter } from "services/marketingFilter.service";


const MarketingDashboard = ({ subcategoryToken: propSubcategoryToken }) => {

    const {
        lastYearSameMonth,
        currentMonthLabel,
        prevMonthLabel
    } = getTimeSeriesLabels();
    const [controller, dispatch] = useMaterialUIController();
    const { miniSidenav } = controller;
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md')); // Below 900px
    const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // Between 900px and 1200px

    // State for API data
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [marketingData, setMarketingData] = useState(null);

    // Filter state for Highlights section time period chips
    const [highlightsActiveIdx, setHighlightsActiveIdx] = React.useState(0);
    const [highlightsActiveChipName, setHighlightsActiveChipName] = useState("YTD");

    // Filter state for Visualizations section time period chips
    const [visualizationActiveIdx, setVisualizationActiveIdx] = React.useState(0);
    const [visualizationActiveChipName, setVisualizationActiveChipName] = useState("YTD");
    const [myDashboardfilterData, setMyDashboardfilterData] = useState(null);

    useEffect(() => {
        const header = document.querySelector('.MuiAppBar-root');
        if (header) {
            header.style.display = 'none';
        }
    }, []);
    const { setFilterOptions, setFilterChips, filterOptions, setActiveFilters, activeFilters } = useFilterContext();


    // Helper function to build GraphQL filters from activeFilters
    const buildGqlFiltersFromActive = useCallback((filters) => {
        const extract = (items) => {
            if (!items || !Array.isArray(items) || items.length === 0) return null;
            // Handle both string arrays and object arrays with name property
            const names = items.map(item => typeof item === 'string' ? item : item?.name).filter(Boolean);
            return names.length > 0 ? { in: names } : null;
        };

        const gqlFiltersRaw = {
            companyName: extract(filters?.Company),
            categoryName: extract(filters?.Category),
            brandName: extract(filters?.Brand),
            subBrandSubcategory: extract(filters?.["Sub-Brand"]),
            subBrandName: extract(filters?.["Sub-Brand"]),
            routeToMarket: extract(filters?.["Route to Market"]),
            channel: extract(filters?.Channel)
        };
        // Remove null/undefined values to avoid backend errors
        return Object.fromEntries(
            Object.entries(gqlFiltersRaw).filter(([_, value]) => value !== null && value !== undefined)
        );
    }, []);

    // Fetch filter options on initial load
    useEffect(() => {
        const fetchFilterOptions = async () => {
            if (filterOptions && Object.keys(filterOptions).length > 0) {
                return; // Already have filter options
            }

            try {
                const marketingFilterResponse = await fetchMarketingFilter();
                const filterOptionsData = {
                    Company: marketingFilterResponse?.companies?.items || [],
                    Category: marketingFilterResponse?.categories?.items || [],
                    Brand: marketingFilterResponse?.brands?.items || [],
                    "Sub-Brand": marketingFilterResponse?.subBrands?.items || [],
                    "Route to Market": marketingFilterResponse?.routeToMarkets?.items || [],
                    Channel: marketingFilterResponse?.channels?.items || [],
                };
                setFilterOptions(filterOptionsData);
                setMyDashboardfilterData(marketingFilterResponse);

                const companyChips = marketingFilterResponse?.companies.items || [];
                const categoryChips = marketingFilterResponse?.categories?.items || [];
                setFilterChips([
                    { key: "all", name: "All" },
                    ...companyChips,
                    ...categoryChips,
                    { key: "custom", name: "Custom" }
                ]);
            } catch (error) {
                console.error('Failed loading filter options:', error);
            }
        };

        fetchFilterOptions();
    }, [filterOptions, setFilterOptions, setFilterChips]);

    // Fetch dashboard data when filters or time periods change
    useEffect(() => {
        const fetchAllData = async () => {
            // Wait for filter options to be available if they're not loaded yet
            if (!filterOptions || Object.keys(filterOptions).length === 0) {
                return;
            }

            try {
                setLoading(true);

                // Build GraphQL filters from activeFilters if they exist and have selections
                // Otherwise, use all available filter options
                let gqlFilters = {};

                if (activeFilters && Object.values(activeFilters).some(arr => (arr || []).length > 0)) {
                    // Use activeFilters when they have selections
                    gqlFilters = buildGqlFiltersFromActive(activeFilters);
                } else {
                    // Fallback to all filter options if no active filters
                    const extractNames = (items) => {
                        if (!items?.length) return null;
                        return {
                            in: items.map(item => typeof item === 'string' ? item : item?.name).filter(Boolean)
                        };
                    };

                    const gqlFiltersRaw = {
                        companyName: extractNames(filterOptions.Company),
                        categoryName: extractNames(filterOptions.Category),
                        brandName: extractNames(filterOptions.Brand),
                        subBrandSubcategory: extractNames(filterOptions["Sub-Brand"]),
                        subBrandName: extractNames(filterOptions?.["Sub-Brand"]),
                        routeToMarket: extractNames(filterOptions["Route to Market"]),
                        channel: extractNames(filterOptions.Channel)
                    };
                    gqlFilters = Object.fromEntries(
                        Object.entries(gqlFiltersRaw).filter(([_, value]) => value !== null && value !== undefined)
                    );
                }

                console.log('Fetching dashboard data with filters:', gqlFilters);
                const marketingResponse = await fetchMarketing(
                    visualizationActiveChipName,
                    highlightsActiveChipName,
                    gqlFilters
                );

                const marketingItem = marketingResponse || null;
                setMarketingData(marketingItem);

            } catch (error) {
                console.error('Failed loading marketing dashboard data:', error);
                setError('Failed to load operations dashboard data');
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [location.search, propSubcategoryToken, visualizationActiveChipName, highlightsActiveChipName, activeFilters, buildGqlFiltersFromActive, filterOptions]);


    const formatToMillions = (value) => {
        return value == null || value === "-" ? "-" : (Number(value) / 1_000_000).toFixed(1);
    }

    const truncateDecimal = (num, decimals = 1) => {
        const factor = Math.pow(10, decimals);
        return Math.floor(num * factor) / factor;
    };

    console.log(marketingData, "marketingData")
    // KPI cards data
    const Kpiscardsdata = [
        {
            title: "Share of Voice (SOV)",
            values: [
                {
                    label: "MTD",
                    change: marketingData?.shareOfVoices?.items?.cMvsGoal ? `${marketingData?.shareOfVoices?.items?.cMvsGoal}%` : "0%",
                    changely: marketingData?.shareOfVoices?.items?.cMvsLM ? `${marketingData?.shareOfVoices?.items?.cMvsLM}%` : "0%",
                    value: `${(((marketingData?.shareOfVoices?.items?.currentMonth ?? 0) / 10000) * 100)
                        .toFixed(1)
                        .replace("0.0", "0")}%`,
                    status: "increase",
                    title: "Month",
                    lyValue: `$${formatToMillions(marketingData?.shareOfVoices?.items?.lastMonth)}M`,
                    glvalue: `$${formatToMillions(marketingData?.shareOfVoices?.items?.goalCurrentMonth)}M`
                },
                {
                    label: "QTD",
                    change: marketingData?.shareOfVoices?.items?.cQvsGoal ? `${marketingData?.shareOfVoices?.items?.cQvsGoal}%` : "0%",
                    changely: marketingData?.shareOfVoices?.items?.cQvsLQ ? `${marketingData?.shareOfVoices?.items?.cQvsLQ}%` : "0%",
                    value: `${(((marketingData?.shareOfVoices?.items?.currentQuarter ?? 0) / 10000) * 100)
                        .toFixed(1)
                        .replace("0.0", "0")}%`,
                    status: "increase",
                    title: "Quarter",
                    lyValue: `$${formatToMillions(marketingData?.shareOfVoices?.items?.lastQuarter)}M`,
                    glvalue: `$${formatToMillions(marketingData?.shareOfVoices?.items?.goalCurrentQuarter)}M`
                },
                {
                    label: "YTD",
                    change: marketingData?.shareOfVoices?.items?.cYvsGoal ? `${marketingData?.shareOfVoices?.items?.cYvsGoal}%` : "0%",
                    changely: marketingData?.shareOfVoices?.items?.cYvsLY ? `${marketingData?.shareOfVoices?.items?.cYvsLY}%` : "0%",
                    value: `${(((marketingData?.shareOfVoices?.items?.currentYear ?? 0) / 10000) * 100)
                        .toFixed(1)
                        .replace("0.0", "0")}%`,
                    status: "increase",
                    title: "Year",
                    lyValue: `$${formatToMillions(marketingData?.shareOfVoices?.items?.lastYear)}M`,
                    glvalue: `$${formatToMillions(marketingData?.shareOfVoices?.items?.goalCurrentYear)}M`
                }
            ]
        },
        {
            title: "Brand Awareness",
            values: [
                {
                    label: "MTD",
                    change: marketingData?.brandAwarenesses?.items?.cMvsGoal ? `${marketingData?.brandAwarenesses?.items?.cMvsGoal}%` : "0%",
                    changely: marketingData?.brandAwarenesses?.items?.cMvsLM ? `${marketingData?.brandAwarenesses?.items?.cMvsLM}%` : "0%",
                    value: `${(((marketingData?.brandAwarenesses?.items?.currentMonth ?? 0) / 10000) * 100)
                        .toFixed(1)
                        .replace("0.0", "0")}%`,
                    status: "increase",
                    title: "Month",
                    lyValue: `$${formatToMillions(marketingData?.brandAwarenesses?.items?.lastMonth)}M`,
                    glvalue: `$${formatToMillions(marketingData?.brandAwarenesses?.items?.goalCurrentMonth)}M`
                },
                {
                    label: "QTD",
                    change: marketingData?.brandAwarenesses?.items?.cQvsGoal ? `${marketingData?.brandAwarenesses?.items?.cQvsGoal}%` : "0%",
                    changely: marketingData?.brandAwarenesses?.items?.cQvsLQ ? `${marketingData?.brandAwarenesses?.items?.cQvsLQ}%` : "0%",
                    value: `${(((marketingData?.brandAwarenesses?.items?.currentQuarter ?? 0) / 10000) * 100)
                        .toFixed(1)
                        .replace("0.0", "0")}%`,
                    status: "increase",
                    title: "Quarter",
                    lyValue: `$${formatToMillions(marketingData?.brandAwarenesses?.items?.lastQuarter)}M`,
                    glvalue: `$${formatToMillions(marketingData?.brandAwarenesses?.items?.goalCurrentQuarter)}M`
                },
                {
                    label: "YTD",
                    change: marketingData?.brandAwarenesses?.items?.cYvsGoal ? `${marketingData?.brandAwarenesses?.items?.cYvsGoal}%` : "0%",
                    changely: marketingData?.brandAwarenesses?.items?.cYvsLY ? `${marketingData?.brandAwarenesses?.items?.cYvsLY}%` : "0%",
                    value: `${(((marketingData?.brandAwarenesses?.items?.currentYear ?? 0) / 10000) * 100)
                        .toFixed(1)
                        .replace("0.0", "0")}%`,
                    status: "increase",
                    title: "Year",
                    lyValue: `$${formatToMillions(marketingData?.brandAwarenesses?.items?.lastYear)}M`,
                    glvalue: `$${formatToMillions(marketingData?.brandAwarenesses?.items?.goalCurrentYear)}M`
                }
            ]
        },
        {
            title: "Market Share % (Dollars Sold)",
            values: [
                {
                    label: "MTD",
                    change: marketingData?.marketSharePcts?.items?.cMvsGoal ? `${marketingData?.marketSharePcts?.items?.cMvsGoal}%` : "0%",
                    changely: marketingData?.marketSharePcts?.items?.cMvsLM ? `${marketingData?.marketSharePcts?.items?.cMvsLM}%` : "0%",
                    value: `${(((marketingData?.marketSharePcts?.items?.currentMonth ?? 0) / 10000) * 100)
                        .toFixed(1)
                        .replace("0.0", "0")}%`,
                    status: "increase",
                    title: "Month",
                    lyValue: `$${formatToMillions(marketingData?.marketSharePcts?.items?.lastMonth)}M`,
                    glvalue: `$${formatToMillions(marketingData?.marketSharePcts?.items?.goalCurrentMonth)}M`
                },
                {
                    label: "QTD",
                    change: marketingData?.marketSharePcts?.items?.cQvsGoal ? `${marketingData?.marketSharePcts?.items?.cQvsGoal}%` : "0%",
                    changely: marketingData?.marketSharePcts?.items?.cQvsLQ ? `${marketingData?.marketSharePcts?.items?.cQvsLQ}%` : "0%",
                    value: `${(((marketingData?.marketSharePcts?.items?.currentQuarter ?? 0) / 10000) * 100)
                        .toFixed(1)
                        .replace("0.0", "0")}%`,
                    status: "increase",
                    title: "Quarter",
                    lyValue: `$${formatToMillions(marketingData?.marketSharePcts?.items?.lastQuarter)}M`,
                    glvalue: `$${formatToMillions(marketingData?.marketSharePcts?.items?.goalCurrentQuarter)}M`
                },
                {
                    label: "YTD",
                    change: marketingData?.marketSharePcts?.items?.cYvsGoal ? `${marketingData?.marketSharePcts?.items?.cYvsGoal}%` : "0%",
                    changely: marketingData?.marketSharePcts?.items?.cYvsLY ? `${marketingData?.marketSharePcts?.items?.cYvsLY}%` : "0%",
                    value: `${(((marketingData?.marketSharePcts?.items?.currentYear ?? 0) / 10000) * 100)
                        .toFixed(1)
                        .replace("0.0", "0")}%`,
                    status: "increase",
                    title: "Year",
                    lyValue: `$${formatToMillions(marketingData?.marketSharePcts?.items?.lastYear)}M`,
                    glvalue: `$${formatToMillions(marketingData?.marketSharePcts?.items?.goalCurrentYear)}M`
                }
            ]
        },
        {
            title: "Customer Acquisition Cost (CAC)",
            values: [
                {
                    label: "MTD",
                    change: marketingData?.customerAcquisitionCosts?.items?.cMvsGoal ? `${marketingData?.customerAcquisitionCosts?.items?.cMvsGoal}%` : "0%",
                    changely: marketingData?.customerAcquisitionCosts?.items?.cMvsLM ? `${marketingData?.customerAcquisitionCosts?.items?.cMvsLM}%` : "0%",
                    value: `${(((marketingData?.customerAcquisitionCosts?.items?.currentMonth ?? 0) / 10000) * 100)
                        .toFixed(1)
                        .replace("0.0", "0")}%`,
                    status: "increase",
                    title: "Month",
                    lyValue: `$${formatToMillions(marketingData?.customerAcquisitionCosts?.items?.lastMonth)}M`,
                    glvalue: `$${formatToMillions(marketingData?.customerAcquisitionCosts?.items?.goalCurrentMonth)}M`
                },
                {
                    label: "QTD",
                    change: marketingData?.customerAcquisitionCosts?.items?.cQvsGoal ? `${marketingData?.customerAcquisitionCosts?.items?.cQvsGoal}%` : "0%",
                    changely: marketingData?.customerAcquisitionCosts?.items?.cQvsLQ ? `${marketingData?.customerAcquisitionCosts?.items?.cQvsLQ}%` : "0%",
                    value: `${(((marketingData?.customerAcquisitionCosts?.items?.currentQuarter ?? 0) / 10000) * 100)
                        .toFixed(1)
                        .replace("0.0", "0")}%`,
                    status: "increase",
                    title: "Quarter",
                    lyValue: `$${formatToMillions(marketingData?.customerAcquisitionCosts?.items?.lastQuarter)}M`,
                    glvalue: `$${formatToMillions(marketingData?.customerAcquisitionCosts?.items?.goalCurrentQuarter)}M`
                },
                {
                    label: "YTD",
                    change: marketingData?.customerAcquisitionCosts?.items?.cYvsGoal ? `${marketingData?.customerAcquisitionCosts?.items?.cYvsGoal}%` : "0%",
                    changely: marketingData?.customerAcquisitionCosts?.items?.cYvsLY ? `${marketingData?.customerAcquisitionCosts?.items?.cYvsLY}%` : "0%",
                    value: `${(((marketingData?.customerAcquisitionCosts?.items?.currentYear ?? 0) / 10000) * 100)
                        .toFixed(1)
                        .replace("0.0", "0")}%`,
                    status: "decrease",
                    title: "Year",
                    lyValue: `$${formatToMillions(marketingData?.customerAcquisitionCosts?.items?.lastYear)}M`,
                    glvalue: `$${formatToMillions(marketingData?.customerAcquisitionCosts?.items?.goalCurrentYear)}M`
                }
            ]
        },
        {
            title: "Customer Lifetime Value (CLV)",
            values: [
                {
                    label: "MTD",
                    change: marketingData?.customerLifetimeValues?.items?.cMvsGoal ? `${marketingData?.customerLifetimeValues?.items?.cMvsGoal}%` : "0%",
                    changely: marketingData?.customerLifetimeValues?.items?.cMvsLM ? `${marketingData?.customerLifetimeValues?.items?.cMvsLM}%` : "0%",
                    value: `${(((marketingData?.customerLifetimeValues?.items?.currentMonth ?? 0) / 10000) * 100)
                        .toFixed(1)
                        .replace("0.0", "0")}%`,
                    status: "increase",
                    title: "Month",
                    lyValue: `$${formatToMillions(marketingData?.customerLifetimeValues?.items?.lastMonth)}M`,
                    glvalue: `$${formatToMillions(marketingData?.customerLifetimeValues?.items?.goalCurrentMonth)}M`

                },
                {
                    label: "QTD",
                    change: marketingData?.customerAcquisitionCosts?.items?.cQvsGoal ? `${marketingData?.customerAcquisitionCosts?.items?.cQvsGoal}%` : "0%",
                    changely: marketingData?.customerAcquisitionCosts?.items?.cQvsLQ ? `${marketingData?.customerAcquisitionCosts?.items?.cQvsLQ}%` : "0%",
                    value: `${(((marketingData?.customerLifetimeValues?.items?.currentQuarter ?? 0) / 10000) * 100)
                        .toFixed(1)
                        .replace("0.0", "0")}%`,
                    status: "increase",
                    title: "Quarter",
                    lyValue: `$${formatToMillions(marketingData?.customerLifetimeValues?.items?.lastQuarter)}M`,
                    glvalue: `$${formatToMillions(marketingData?.customerLifetimeValues?.items?.goalCurrentQuarter)}M`
                },
                {
                    label: "YTD",
                    change: marketingData?.customerLifetimeValues?.items?.cYvsGoal ? `${marketingData?.customerLifetimeValues?.items?.cYvsGoal}%` : "0%",
                    changely: marketingData?.customerLifetimeValues?.items?.cYvsLY ? `${marketingData?.customerLifetimeValues?.items?.cYvsLY}%` : "0%",
                    value: `${(((marketingData?.customerLifetimeValues?.items?.currentYear ?? 0) / 10000) * 100)
                        .toFixed(1)
                        .replace("0.0", "0")}%`,
                    status: "decrease",
                    title: "Year",
                    lyValue: `$${formatToMillions(marketingData?.customerLifetimeValues?.items?.lastYear)}M`,
                    glvalue: `$${formatToMillions(marketingData?.customerLifetimeValues?.items?.goalCurrentYear)}M`
                }
            ]
        },
        {
            title: "Net Promoter Score (NPS)",
            values: [
                {
                    label: "MTD",
                    change: marketingData?.netPromoterScores?.items?.cMvsGoal ? `${marketingData?.netPromoterScores?.items?.cMvsGoal}%` : "0%",
                    changely: marketingData?.netPromoterScores?.items?.cMvsLM ? `${marketingData?.netPromoterScores?.items?.cMvsLM}%` : "0%",
                    value: `${(((marketingData?.netPromoterScores?.items?.currentMonth ?? 0) / 10000) * 100)
                        .toFixed(1)
                        .replace("0.0", "0")}%`,
                    status: "increase",
                    title: "Month",
                    lyValue: `$${formatToMillions(marketingData?.netPromoterScores?.items?.lastMonth)}M`,
                    glvalue: `$${formatToMillions(marketingData?.netPromoterScores?.items?.goalCurrentMonth)}M`
                },
                {
                    label: "QTD",
                    change: marketingData?.netPromoterScores?.items?.cQvsGoal ? `${marketingData?.netPromoterScores?.items?.cQvsGoal}%` : "0%",
                    changely: marketingData?.netPromoterScores?.items?.cQvsLQ ? `${marketingData?.netPromoterScores?.items?.cQvsLQ}%` : "0%",
                    value: `${(((marketingData?.netPromoterScores?.items?.currentQuarter ?? 0) / 10000) * 100)
                        .toFixed(1)
                        .replace("0.0", "0")}%`,
                    status: "increase",
                    title: "Quarter",
                    lyValue: `$${formatToMillions(marketingData?.netPromoterScores?.items?.lastQuarter)}M`,
                    glvalue: `$${formatToMillions(marketingData?.netPromoterScores?.items?.goalCurrentQuarter)}M`
                },
                {
                    label: "YTD",
                    change: marketingData?.netPromoterScores?.items?.cYvsGoal ? `${marketingData?.netPromoterScores?.items?.cYvsGoal}%` : "0%",
                    changely: marketingData?.netPromoterScores?.items?.cYvsLY ? `${marketingData?.netPromoterScores?.items?.cYvsLY}%` : "0%",
                    value: `${(((marketingData?.netPromoterScores?.items?.currentYear ?? 0) / 10000) * 100)
                        .toFixed(1)
                        .replace("0.0", "0")}%`,
                    status: "increase",
                    title: "Year",
                    lyValue: `$${formatToMillions(marketingData?.netPromoterScores?.items?.lastYear)}M`,
                    glvalue: `$${formatToMillions(marketingData?.netPromoterScores?.items?.goalCurrentYear)}M`
                }
            ]
        }
    ];


    //second section cards
    const timeSeriesCardsdata = [
        {
            title: "Share of Voice (SOV)",
            values: [
                {
                    label: lastYearSameMonth,
                    change: marketingData?.shareOfVoices?.items?.cmcYvsCMLY ? `${truncateDecimal(marketingData?.shareOfVoices?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(marketingData?.shareOfVoices?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: null,
                    value: `$${formatToMillions(marketingData?.shareOfVoices?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: marketingData?.shareOfVoices?.items?.cMvsLM ? `${truncateDecimal(marketingData?.shareOfVoices?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(marketingData?.shareOfVoices?.items?.lastMonth)}M`,
                    status: "increase",
                    arrow: "left"
                }
            ]
        },
        {
            title: "Brand Awareness",
            values: [
                {
                    label: lastYearSameMonth,
                    change: marketingData?.brandAwarenesses?.items?.cmcYvsCMLY ? `${truncateDecimal(marketingData?.brandAwarenesses?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(marketingData?.brandAwarenesses?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: null,
                    value: `$${formatToMillions(marketingData?.brandAwarenesses?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: marketingData?.brandAwarenesses?.items?.cMvsLM ? `${truncateDecimal(marketingData?.brandAwarenesses?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(marketingData?.brandAwarenesses?.items?.lastMonth)}M`,
                    status: "increase",
                    arrow: "left"
                }
            ]
        },
        {
            title: "Market Share (Dollar Sales)",
            values: [
                {
                    label: lastYearSameMonth,
                    change: marketingData?.marketSharePcts?.items?.cmcYvsCMLY ? `${truncateDecimal(marketingData?.marketSharePcts?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(marketingData?.marketSharePcts?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: null,
                    value: `$${formatToMillions(marketingData?.marketSharePcts?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: marketingData?.marketSharePcts?.items?.cMvsLM ? `${truncateDecimal(marketingData?.marketSharePcts?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(marketingData?.marketSharePcts?.items?.lastMonth)}M`,
                    status: "increase",
                    arrow: "left"
                }
            ]
        },
        {
            title: "Customer Acquisition Cost (CAC)",
            values: [
                {
                    label: lastYearSameMonth,
                    change: marketingData?.customerAcquisitionCosts?.items?.cmcYvsCMLY ? `${truncateDecimal(marketingData?.customerAcquisitionCosts?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(marketingData?.customerAcquisitionCosts?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: null,
                    value: `$${formatToMillions(marketingData?.customerAcquisitionCosts?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: marketingData?.customerAcquisitionCosts?.items?.cMvsLM ? `${truncateDecimal(marketingData?.customerAcquisitionCosts?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(marketingData?.customerAcquisitionCosts?.items?.lastMonth)}M`,
                    status: "increase",
                    arrow: "left"
                }
            ]
        },
        {
            title: "Customer Lifetime Value (CLV)",
            values: [
                {
                    label: lastYearSameMonth,
                    change: marketingData?.customerLifetimeValues?.items?.cmcYvsCMLY ? `${truncateDecimal(marketingData?.customerLifetimeValues?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(marketingData?.customerLifetimeValues?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: null,
                    value: `$${formatToMillions(marketingData?.customerLifetimeValues?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: marketingData?.customerLifetimeValues?.items?.cMvsLM ? `${truncateDecimal(marketingData?.customerLifetimeValues?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(marketingData?.customerLifetimeValues?.items?.lastMonth)}M`,
                    status: "increase",
                    arrow: "left"
                }
            ]
        },
        {
            title: "Net Promoter Score (NPS)",
            values: [
                {
                    label: lastYearSameMonth,
                    change: marketingData?.netPromoterScores?.items?.cmcYvsCMLY ? `${truncateDecimal(marketingData?.netPromoterScores?.items?.cmcYvsCMLY)}%` : "0%",
                    value: `$${formatToMillions(marketingData?.netPromoterScores?.items?.sameMonthLastYear)}M`,
                    status: "increase",
                    arrow: "right"
                },
                {
                    label: currentMonthLabel,
                    change: null,
                    value: `$${formatToMillions(marketingData?.netPromoterScores?.items?.currentMonth)}M`,
                    status: "nochange",
                    arrow: null
                },
                {
                    label: prevMonthLabel,
                    change: marketingData?.netPromoterScores?.items?.cMvsLM ? `${truncateDecimal(marketingData?.netPromoterScores?.items?.cMvsLM)}%` : "0%",
                    value: `$${formatToMillions(marketingData?.netPromoterScores?.items?.lastMonth)}M`,
                    status: "increase",
                    arrow: "left"
                }
            ]
        }
    ];




    const [openFilters, setOpenFilters] = useState(false);
    const handleToggleFilters = () => {
        setOpenFilters((prev) => !prev);
    };

    // Filter state
    const [activeFilter, setActiveFilter] = useState('All');
    const [filterLoading, setFilterLoading] = useState(false);

    // Define filter presets for each quick filter
    const getFilterPreset = useCallback((filterName) => {
        const allCompanies = ["Nutrabolt", "Bloom"];
        const allCategories = ["Functional Beverage", "Active Nutrition"];
        const allBrands = ["C4", "Cellucor", "XTEND", "Bloom"];
        const allSubBrands = ["C4", "C4 Performance", "C4 Smart Energy", "C4 Ultimate", "XTEND", "Bloom Sparkling Energy", "Bloom Pop"];
        const allRoutes = ["Ecommerce", "Direct", "DSD"];
        const allChannels = ["Ecommerce", "Convenience Chain", "Convenience Independent", "Drug", "Food", "Multi Outlet", "Mass"];

        switch (filterName) {
            case 'All':
                return {
                    Company: allCompanies,
                    Category: allCategories,
                    Brand: allBrands,
                    "Sub-Brand": allSubBrands,
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Nutrabolt':
                return {
                    Company: ["Nutrabolt"],
                    Category: allCategories,
                    Brand: ["C4", "Cellucor", "XTEND"],
                    "Sub-Brand": ["C4", "C4 Performance", "C4 Smart Energy", "C4 Ultimate", "XTEND"],
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Bloom':
                return {
                    Company: ["Bloom"],
                    Category: ["Functional Beverage"],
                    Brand: ["Bloom"],
                    "Sub-Brand": ["Bloom Sparkling Energy", "Bloom Pop"],
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Functional Beverage':
                return {
                    Company: allCompanies,
                    Category: ["Functional Beverage"],
                    Brand: ["C4", "Bloom"],
                    "Sub-Brand": ["C4 Performance", "C4 Smart Energy", "C4 Ultimate", "Bloom Sparkling Energy", "Bloom Pop"],
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            case 'Active Nutrition':
                return {
                    Company: ["Nutrabolt"],
                    Category: ["Active Nutrition"],
                    Brand: ["C4", "Cellucor", "XTEND"],
                    "Sub-Brand": ["C4", "XTEND"],
                    "Route to Market": allRoutes,
                    Channel: allChannels,
                };
            default:
                return null;
        }
    }, []);

    const [filterPresets, setFilterPresets] = useState(() => getFilterPreset('All'));

    // Handle filter click with full-page loader
    const handleFilterClick = (filter) => {
        if (filter === activeFilter || filterLoading) return;

        setFilterLoading(true);
        setLoading(true); // Show full-page loader

        // Simulate loading delay
        setTimeout(() => {
            const preset = getFilterPreset(filter);
            setActiveFilter(filter);

            // Set the preset - this will trigger FilterPanelContainer to apply it
            if (preset) {
                setFilterPresets(preset);
            } else {
                setFilterPresets(null);
            }

            setFilterLoading(false);
            setLoading(false); // Hide full-page loader

            // Open filter panel for "Custom" filter
            if (filter === 'Custom') {
                setOpenFilters(true);
            }
        }, 800);
    };

    // Handle filter changes from FilterPanelContainer
    const handleFiltersChange = useCallback((newFilters) => {
        // Don't check for preset match if we're already in Custom mode
        // FilterPanelContainer's isApplyingPresetRef prevents callback when applying presets
        if (activeFilter === 'Custom') return;

        // Check if the current filters match any preset
        const presetNames = ['All', 'Nutrabolt', 'Bloom', 'Functional Beverage', 'Active Nutrition'];
        let matchedPreset = null;

        for (const presetName of presetNames) {
            const preset = getFilterPreset(presetName);
            if (preset) {
                const isMatching = Object.keys(preset).every(key => {
                    const presetValues = [...(preset[key] || [])].sort();
                    const currentValues = [...(newFilters[key] || [])].sort();
                    return presetValues.length === currentValues.length &&
                        presetValues.every((val, idx) => val === currentValues[idx]);
                });

                if (isMatching) {
                    matchedPreset = presetName;
                    break;
                }
            }
        }

        // If filters don't match any preset, switch to Custom
        if (!matchedPreset && activeFilter !== 'Custom') {
            setActiveFilter('Custom');
            setFilterPresets(null);
        }
    }, [activeFilter, getFilterPreset]);
    const [activeIdx, setActiveIdx] = React.useState(0);
    const kpiBriefData = [
        {
            title: "Month",
            value: "$ 65.2 M",
            lyValue: "59.12 M",
            pct: "5.8%",
            positive: true,
            status: "increase",
            glvalue: "$ 85.5 M"
        },
        {
            title: "Quarter",
            value: "$ 180.6 M",
            lyValue: "160.29 M",
            pct: "11.9%",
            positive: true,
            status: "increase",
            glvalue: "$ 207.3 M"
        },
        {
            title: "Year",
            value: "$ 859.1 M",
            lyValue: "823.03 M",
            pct: "5.6%",
            positive: true,
            status: "increase",
            glvalue: "$ 882.8 M"
        }
    ];

    const getSpendRangeColor = (spendRange) => {
        if (!spendRange) return "#000000";

        const normalized = spendRange.trim();

        const colorMap = {
            "$75K–150K": "#4A63E0",
            "$150K–300K": "#2E3BA8",
            "$300K+": "#1C275F",
            "$0–25K": "#A3B8FF",
            "$25K–75K": "#6F8DFF",
        };

        return colorMap[normalized] || "#000000";
    };
    const marketingSpendPie = useMemo(() => {
        const items = marketingData?.marketingSpendByRanges?.items;

        if (!items || !Array.isArray(items) || items.length === 0) {
            return [];
        }

        return items.map((item) => ({
            label: item.spendRange || "",
            value: item.ytdPct || 0, // Changed from mtdValue → ytdPct
            color: getSpendRangeColor(item.spendRange),
        }));
    }, [marketingData]);

    console.log(marketingSpendPie, "marketingSpendPie")


    const getTrafficSourceColor = (sourceName) => {
        const colorMap = {
            "Referral": "#F2A93B",
            "Paid Search": "#64719A",
            "Organic Search": "#3FAF6C",
            "Paid Social": "#E46BAA",
            "Direct": "#4A63E0",
        };

        return colorMap[sourceName?.trim()] || "#000000";
    };
    const websiteTrafficPie = useMemo(() => {
        const items = marketingData?.websiteTrafficBySources?.items;

        if (!items || !Array.isArray(items) || items.length === 0) {
            return [];
        }

        return items.map((item) => ({
            label: item.trafficSourceName || "",
            value: item.ytdPct || 0, // change to mtdPct/qtdPct if needed
            color: getTrafficSourceColor(item.trafficSourceName),
        }));
    }, [marketingData]);
    console.log(websiteTrafficPie, "websiteTrafficPie")




    const cacBreakdown = [

        { label: "Retail Media", value: 16, color: "#7ED321" },
        { label: "Influencers", value: 10, color: "#E1B649" },
        { label: "Amazon Ads", value: 10, color: "#BD10E0" },
        { label: "Email/SMS", value: 5, color: "#64719A" },
        { label: "Paid Social", value: 32, color: "#4A90E2" },
        { label: "Paid Search", value: 29, color: "#F2A93B" },

    ];
    const getCACChannelColor = (channelName) => {

        const normalized = channelName?.trim().toLowerCase();

        const colorMap = {
            "retail media": "#7ED321",
            "influencers (paid)": "#E1B649",
            "amazon ads": "#BD10E0",
            "email/sms": "#64719A",
            "paid social": "#4A90E2",
            "paid search": "#F2A93B",
        };

        return colorMap[normalized] || "#000000";
    };
    const casbraekdowns = useMemo(() => {
        const items = marketingData?.cacBreakdownByChannels?.items;

        if (!items || !Array.isArray(items) || items.length === 0) {
            return [];
        }

        return items.map((item) => ({
            label: item.channelName || "",
            value: item.ytdPct || 0,
            color: getCACChannelColor(item.channelName),
        }));
    }, [marketingData]);
    console.log(casbraekdowns, "casbraekdowns")


    const getRegionColor = (regionName) => {
        const colorMap = {
            "West": "#3BA73B",
            "North": "#B5CBEA",
            "South": "#9B72C2",
            "South Central": "#FFC081",
            "Northeast": "#D93737",
            "Central": "#DEDE96",
        };

        return colorMap[regionName?.trim()] || "#000000";
    };
    const impressionsRegionPie = useMemo(() => {
        const items = marketingData?.impressionsByRegions?.items;

        if (!items || !Array.isArray(items) || items.length === 0) {
            return [];
        }

        return items.map((item) => ({
            label: item.regionName || "",
            value: item.ytdPct || 0,
            color: getRegionColor(item.regionName),
        }));
    }, [marketingData]);
    console.log(impressionsRegionPie, "impressionsRegionPie")




    const getChannelColor = (channelName) => {
        const normalized = channelName?.trim().toLowerCase();

        const colorMap = {
            "paid social": "#1A73E8",
            "influencers (paid)": "#F6C344",
            "retail media": "#34A853",
            "amazon ads": "#8B5CF6",
            "programmatic / display": "#D93025",
            "programmatic/display": "#D93025"
        };

        return colorMap[normalized] || "#000000";
    };

    const convertImpressionsByChannel = useMemo(() => {
        const items = marketingData?.impressionsByChannelOverTime?.items;

        if (!Array.isArray(items) || items.length === 0) return null;

        // Sort months
        const sortedItems = [...items].sort((a, b) =>
            a.month.localeCompare(b.month)
        );

        // Format months correctly
        const formatMonth = (yearMonth) => {
            const [year, month] = yearMonth.split("-");
            const monthNames = [
                "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
            ];
            return `${monthNames[parseInt(month, 10) - 1]}. ${year}`;
        };

        const xLabels = sortedItems.map((item) =>
            formatMonth(item.month)
        );

        // Get all unique channels
        const channelSet = new Set();
        sortedItems.forEach((item) => {
            item.channels?.forEach((ch) => {
                channelSet.add(ch.channel);
            });
        });

        const channels = Array.from(channelSet);

        // Series
        const series = channels.map((channel) => ({
            label: channel,
            color: getChannelColor(channel),
        }));

        // Data Matrix
        const dataMatrix = sortedItems.map((item) => {
            return channels.map((channel) => {
                const match = item.channels?.find(
                    (ch) => ch.channel === channel
                );
                return match?.impressionValue ?? 0;
            });
        });

        // Calculate stacked totals per month
        const stackedTotals = dataMatrix.map(row =>
            row.reduce((sum, val) => sum + val, 0)
        );

        // Get maximum stacked value
        const maxValue = Math.max(...stackedTotals);

        // Generate nice tick step
        function niceTickStep(maxVal, numTicks = 5) {
            const roughStep = maxVal / numTicks;
            const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
            const residual = roughStep / magnitude;

            let niceResidual;
            if (residual >= 5) niceResidual = 5;
            else if (residual >= 2) niceResidual = 2;
            else niceResidual = 1;

            return niceResidual * magnitude;
        }

        const tickStep = niceTickStep(maxValue, 5);
        const maxTick = tickStep * Math.ceil(maxValue / tickStep);

        const yTicks = [];
        for (let tick = 0; tick <= maxTick; tick += tickStep) {
            yTicks.push(tick);
        }

        console.log(yTicks, "yTicks")
        return {
            title: "Impressions by Channel Over Time",
            xLabels,
            series,
            dataMatrix,
            yTicks,
        };
    }, [marketingData]);

    console.log(convertImpressionsByChannel, "convertImpressionsByChannel")

    const getMarketingChannelColor = (channel) => {
        const colorMap = {
            "Paid Social": "#4285F4",
            "Influencers (Paid)": "#F5A623",
            "Retail Media": "#7ED321",
            "Amazon Ads": "#BD10E0",
            "Programmatic / Display": "#D0021B",
        };

        return colorMap[channel] || "#000000";
    };
    const skuBarchartDatas = useMemo(() => {
        const items =
            marketingData?.impressionsTop10Campaigns?.items;

        if (!Array.isArray(items) || items.length === 0) return null;

        // Y-axis labels (Campaign Names)
        const yLabels = [...new Set(items.map((i) => i.campaignName))];

        // Channel labels
        const channelLabels = [...new Set(items.map((i) => i.channelName))];

        // Fixed channel order
        const channelOrder = [
            "Paid Social",
            "Influencers (Paid)",
            "Retail Media",
            "Amazon Ads",
            "Programmatic / Display",
        ];

        const sortedChannels = channelOrder.filter((c) =>
            channelLabels.includes(c)
        );

        // Series with colors
        const series = sortedChannels.map((channel) => ({
            label: channel,
            color: getMarketingChannelColor(channel),
        }));

        // Optimized Lookup Map (Better than .find())
        const lookup = {};
        items.forEach((i) => {
            lookup[`${i.campaignName}-${i.channelName}`] =
                i.ytdValue ?? 0;
        });

        // data Matrix (rows = campaigns, columns = channels)
        const dataMatrix = yLabels.map((campaign) =>
            sortedChannels.map(
                (channel) =>
                    lookup[`${campaign}-${channel}`] ?? 0
            )
        );
        const totals = dataMatrix.map((row) =>
            row.reduce((sum, val) => sum + val, 0)
        );

        const maxValue = Math.max(...totals);

        const generateTicks = (max) => {
            if (!max) return [0];

            const step = Math.ceil(max / 5);
            const roundedStep =
                Math.pow(10, Math.floor(Math.log10(step))) *
                Math.ceil(step / Math.pow(10, Math.floor(Math.log10(step))));

            const ticks = [];
            for (let i = 0; i <= 5; i++) {
                ticks.push(i * roundedStep);
            }

            return ticks;
        };

        const xTicks = generateTicks(maxValue);

        return {
            title: "Impressions by Top 10 Campaigns",
            yLabels,
            series,
            dataMatrix,
            xTicks,
        };
    }, [marketingData]);

    console.log(skuBarchartDatas, "skuBarchartData")

    const [hover, setHover] = React.useState(false);

    const stateNameToCode = {
        Alabama: "AL",
        Alaska: "AK",
        Arizona: "AZ",
        Arkansas: "AR",
        California: "CA",
        Colorado: "CO",
        Connecticut: "CT",
        Delaware: "DE",
        Florida: "FL",
        Georgia: "GA",
        Hawaii: "HI",
        Idaho: "ID",
        Illinois: "IL",
        Indiana: "IN",
        Iowa: "IA",
        Kansas: "KS",
        Kentucky: "KY",
        Louisiana: "LA",
        Maine: "ME",
        Maryland: "MD",
        Massachusetts: "MA",
        Michigan: "MI",
        Minnesota: "MN",
        Mississippi: "MS",
        Missouri: "MO",
        Montana: "MT",
        Nebraska: "NE",
        Nevada: "NV",
        New_Hampshire: "NH",
        New_Jersey: "NJ",
        New_Mexico: "NM",
        New_York: "NY",
        North_Carolina: "NC",
        North_Dakota: "ND",
        Ohio: "OH",
        Oklahoma: "OK",
        Oregon: "OR",
        Pennsylvania: "PA",
        Rhode_Island: "RI",
        South_Carolina: "SC",
        South_Dakota: "SD",
        Tennessee: "TN",
        Texas: "TX",
        Utah: "UT",
        Vermont: "VT",
        Virginia: "VA",
        Washington: "WA",
        West_Virginia: "WV",
        Wisconsin: "WI",
        Wyoming: "WY",
    };
    const thickBorderStates = [
        "CA", "AZ", "NM", "TX", "OK", "MO", "IL",
        "IN", "KY", "TN", "GA", "NC", "VA"
    ];
    const legendData = [
        { label: "$0–$5M", color: "#C8C6C4", min: 0, max: 5000000 },
        { label: "$5M–$10M", color: "#D0EBBE", min: 5000000, max: 10000000 },
        { label: "$10M–$20M", color: "#B7E29A", min: 10000000, max: 20000000 },
        { label: "$20M–$35M", color: "#99D56F", min: 20000000, max: 35000000 },
        { label: "$35M–$50M", color: "#6BB542", min: 35000000, max: 50000000 },
        { label: "$50M+", color: "#4C8C17", min: 50000000, max: Infinity },
    ];
    const usemapData = useMemo(() => {
        const items = marketingData?.impressionsByStates?.items;

        const usaData = {};
        if (Array.isArray(items)) {
            items.forEach(item => {
                const stateCode = stateNameToCode[item.stateCode] || item.stateCode;
                usaData[stateCode] = item.ytdValue ?? 0;
            });
        }

        return {
            usaData, // will at least be an empty object
            thickBorderStates,
            legendData,
        };
    }, [marketingData]);

    console.log(usemapData, "usedata")


    return (

        <Box sx={{
            ml: 0,
            transition: "margin-left 0.25s ease",
            "& .MuiBox-root": { backgroundImage: "none" }
        }} style={{
            padding: isMobile ? '8px' : '0px 16px 0px 16px',
            fontFamily: 'inter !important'
        }}>
            {/* Loading Backdrop */}
            <Backdrop
                open={loading}
                sx={{
                    position: 'fixed',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }}
            >
                <CircularProgress color="primary" />
            </Backdrop>

            <Box
                sx={{
                    height: {
                        xs: "calc(100dvh - 140px)", // mobile header
                        md: "calc(100vh - 64px)",  // desktop header
                    },
                    overflowY: "auto",
                    // px: isMobile ? 1 : 2,
                    pb: "100px",
                    /* smooth scrolling */
                    scrollBehavior: "smooth",
                    WebkitOverflowScrolling: "touch", // iOS momentum

                    /* hide scrollbar */
                    scrollbarWidth: "none",          // Firefox
                    msOverflowStyle: "none",         // IE / Edge
                    "&::-webkit-scrollbar": {
                        display: "none",               // Chrome / Safari
                    },
                }}
            >
                {/* Section Title: KPI's */}
                <Typography sx={{ fontSize: '16px', mb: 1, mt: 3 }}>
                    <span style={{ color: '#1672B980', fontWeight: 200, fontSize: "16px" }}>Marketing / </span><span style={{ fontWeight: 200, fontSize: "16px" }}>KPI's</span>
                </Typography>
                <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                <KpiMetricscards cardsData={Kpiscardsdata} kpiBriefData={kpiBriefData} />

                {/* Section Title: Time Series */}
                <Typography sx={{ fontSize: '16px', mb: 1, mt: 3 }}>
                    <span style={{ color: '#1672B980', fontWeight: 200, fontSize: "16px" }}>Marketing / </span><span style={{ fontWeight: 200, fontSize: "16px" }}>Time Series</span>
                </Typography>
                <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                <TimeSeriescards timeSeriesCards={timeSeriesCardsdata} />




                <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography sx={{ fontSize: '16px' }}>
                            <span style={{ color: "#1672B980", fontWeight: 200, fontSize: "16px" }}>Marketing / </span> <span style={{ fontSize: "16px", fontWeight: 200 }}>Visualizations</span>
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {["YTD", "QTD", "MTD"].map((label, idx) => (
                                <FilterChip
                                    key={label}
                                    label={label}
                                    idx={idx}
                                    activeIdx={activeIdx}
                                    onClick={setActiveIdx}
                                />
                            ))}
                        </Box>
                    </Box>

                    <Box sx={{ width: '100%', height: '1px', background: '#1672B91A', borderRadius: '2px', mb: 0 }} />
                </Box>
                <Grid
                    container
                    rowSpacing={2}
                    columnSpacing={2}
                    sx={{
                        mt: "26px !important",
                        alignItems: "stretch"   // keeps all cards same height
                    }}
                >
                    <Grid item xs={12} sm={6} md={3} sx={{
                        pt: { xs: "0px !important", sm: "0px !important", md: "0px !important" }
                    }} >
                        <PieLegendCard title="Marketing Spend" data={marketingSpendPie} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3} sx={{
                        pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                    }}>
                        <PieLegendCard title="Website Traffic by Source" data={websiteTrafficPie} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3} sx={{
                        pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                    }}>
                        <PieLegendCard title="Customer Acquisition Cost Breakdown" data={casbraekdowns} />
                    </Grid>

                    <Grid item xs={12} sm={6} md={3} sx={{
                        pt: { xs: "26px !important", sm: "26px !important", md: "0px !important" }
                    }} >
                        <PieLegendCard title="Impressions by Region" data={impressionsRegionPie} />
                    </Grid>
                </Grid>
                <Grid container spacing={3} sx={{ mt: "10px !important" }}>
                    {/* LEFT CARD - USA MAP */}
                    <Grid item xs={12} md={4}>
                        <Map
                            title="Impressions by State Heatmap"
                            usaData={usemapData?.usaData}
                            thickBorderStates={usemapData?.thickBorderStates}
                            legendData={usemapData?.legendData}
                        />

                    </Grid>

                    {/* RIGHT CARD - BAR CHART */}
                    <Grid item xs={12} md={8}>
                        {skuBarchartDatas?.title &&
                            skuBarchartDatas?.yLabels?.length > 0 &&
                            skuBarchartDatas?.series?.length > 0 &&
                            skuBarchartDatas?.dataMatrix?.length > 0 && (
                                <SkuSingleBarChart
                                    sx={{ flex: 1 }}
                                    title={skuBarchartDatas.title}
                                    yLabels={skuBarchartDatas.yLabels}
                                    series={skuBarchartDatas.series}
                                    dataMatrix={skuBarchartDatas.dataMatrix}
                                    xTicks={skuBarchartDatas.xTicks}
                                    showUnit={true}
                                />
                            )}

                    </Grid>
                </Grid>
                <Grid container sx={{ mt: "34px !important" }}>
                    <Grid item xs={12}>
                        {convertImpressionsByChannel?.xLabels?.length > 0 &&
                            convertImpressionsByChannel?.series?.length > 0 &&
                            convertImpressionsByChannel?.dataMatrix?.length > 0 && (

                                <Barcharts
                                    title={convertImpressionsByChannel.title}
                                    xLabels={convertImpressionsByChannel.xLabels}
                                    series={convertImpressionsByChannel.series}
                                    dataMatrix={convertImpressionsByChannel.dataMatrix}
                                    yTicks={convertImpressionsByChannel?.yTicks}   // adjust based on your max value
                                    yAxisLabel="Count (M)"
                                    valueFormatter={(v) => `${v}`}
                                    rounded={false}
                                />

                            )}

                    </Grid>
                </Grid>

            </Box>


        </Box>



    );
};

export default MarketingDashboard;